/**
 * SpeechSynthesis utility for the Therapist AI web app
 * Provides a consistent interface for the Web Speech API's speech synthesis functionality
 * with fallbacks, cross-browser compatibility, and animation synchronization
 */

class SpeechSynthesisService {
  constructor() {
    // Initialize properties
    this.synthesis = window.speechSynthesis;
    this.utterance = null;
    this.isSpeaking = false;
    this.isSupported = typeof this.synthesis !== 'undefined';
    this.voice = null;
    this.voicesLogged = false; 
    this.voicesLoaded = false; // New flag to indicate voices are ready
    this.pitch = 1.0;
    this.rate = 1.0;
    this.volume = 1.0;
    this.availableVoices = []; // Store loaded voices
    this._pendingVoiceSetting = null; // To store a pending setVoiceByGender call
    this.soundEnabled = true; // Assume sound is enabled by default

    // Animation-related properties
    this.phonemeCallbacks = new Map();
    this.wordCallbacks = new Map();
    this.visemeMap = this.createVisemeMap();
    
    // Callbacks
    this.onStartCallback = null;
    this.onEndCallback = null;
    this.onPauseCallback = null;
    this.onResumeCallback = null;
    this.onErrorCallback = null;
    this.onBoundaryCallback = null;
    this.onMarkCallback = null;

    // Ensure voices are loaded
    this._loadVoices();
  }

  _loadVoices() {
    if (!this.isSupported) return;

    const populateVoices = () => {
      this.availableVoices = this.synthesis.getVoices();
      if (this.availableVoices.length > 0) {
        this.voicesLoaded = true; // Set flag when voices are available
        if (!this.voicesLogged) {
            console.log(
              "Available Speech Synthesis Voices:",
              this.availableVoices.map(v => ({ name: v.name, lang: v.lang, default: v.default, localService: v.localService, voiceURI: v.voiceURI }))
            );
            this.voicesLogged = true;
        }
        // If there's a pending voice setting, try it now
        if (this._pendingVoiceSetting) {
            this.setVoiceByGender(this._pendingVoiceSetting.gender, this._pendingVoiceSetting.lang);
            this._pendingVoiceSetting = null;
        }
      }
    };

    // Voices might be loaded asynchronously.
    if (this.synthesis.onvoiceschanged !== undefined) {
      this.synthesis.onvoiceschanged = populateVoices;
    }
    // Call it once directly too, in case they are already loaded or onvoiceschanged doesn't fire.
    populateVoices(); 
    // And try again after a short delay for some browsers
    setTimeout(populateVoices, 500);
  }
  
  /**
   * Create a mapping from phonemes to visemes (mouth shapes)
   * @returns {Map} A map of phonemes to viseme identifiers
   */
  createVisemeMap() {
    const map = new Map();
    
    // Map English phonemes to basic visemes (mouth shapes)
    // A, E, I sounds
    ['a', 'æ', 'eɪ', 'ɛ', 'i', 'ɪ'].forEach(phoneme => map.set(phoneme, 'a-e-i'));
    
    // O, U sounds
    ['o', 'oʊ', 'ɔ', 'u', 'ʊ'].forEach(phoneme => map.set(phoneme, 'o-u'));
    
    // F, V sounds
    ['f', 'v'].forEach(phoneme => map.set(phoneme, 'f-v'));
    
    // Th sounds
    ['θ', 'ð'].forEach(phoneme => map.set(phoneme, 'th'));
    
    // L sounds
    ['l'].forEach(phoneme => map.set(phoneme, 'l'));
    
    // S, Z sounds
    ['s', 'z'].forEach(phoneme => map.set(phoneme, 's-z'));
    
    // R sounds
    ['r'].forEach(phoneme => map.set(phoneme, 'r'));
    
    // W sounds
    ['w'].forEach(phoneme => map.set(phoneme, 'w'));
    
    // M, B, P sounds
    ['m', 'b', 'p'].forEach(phoneme => map.set(phoneme, 'm-b-p'));
    
    // Default closed mouth
    map.set('default', 'closed');
    
    return map;
  }
  
  /**
   * Get available voices and filter by language if specified
   * @param {string} lang - Optional language code to filter voices
   * @returns {Array} Array of available voice objects
   */
  getVoices(lang = null) {
    // Ensure voices are loaded if they haven't been logged yet (might be an initial call)
    if (this.availableVoices.length === 0 && !this.voicesLoaded) { // Check voicesLoaded flag
        this._loadVoices(); // Attempt to load/log them again
    }

    if (lang) {
      return this.availableVoices.filter(voice => voice.lang.startsWith(lang));
    }
    return this.availableVoices;
  }
  
  /**
   * Set the voice by name, language, or index
   * @param {string|number} voice - Voice identifier (name, language, or index)
   * @returns {boolean} Whether the voice was successfully set
   */
  setVoice(voice) {
    const voices = this.getVoices();
    
    if (!voices.length) {
      return false;
    }
    
    // If voice is a number, use it as an index
    if (typeof voice === 'number') {
      if (voice >= 0 && voice < voices.length) {
        this.voice = voices[voice];
        return true;
      }
      return false;
    }
    
    // If voice is a string, try to find by name or language
    if (typeof voice === 'string') {
      // Try to find by name
      let selectedVoice = voices.find(v => v.name === voice);
      
      // If not found by name, try by language
      if (!selectedVoice) {
        selectedVoice = voices.find(v => v.lang.startsWith(voice));
      }
      
      if (selectedVoice) {
        this.voice = selectedVoice;
        return true;
      }
      return false;
    }
    
    return false;
  }
  
  /**
   * Set a gender-appropriate voice
   * @param {string} gender - 'male' or 'female'
   * @param {string} lang - Language code (e.g., 'en-US')
   * @returns {boolean} Whether a suitable voice was found and set
   */
  setVoiceByGender(gender, lang = 'en-US') {
    // If voices are not yet loaded, store the request and try again when they are.
    if (!this.voicesLoaded) {
        this._pendingVoiceSetting = { gender, lang };
        console.log(`SpeechSynthesisService: Voices not yet loaded. Deferring setVoiceByGender for ${gender}.`);
        return false;
    }

    const voices = this.getVoices(lang);
    
    if (!voices.length) {
      console.warn(`SpeechSynthesisService: No voices available for lang ${lang} after loading.`);
      this.voice = null; // Ensure voice is explicitly null
      return false;
    }
    
    let selectedVoice = null;
    
    // Set initial pitch and rate based on gender, these will be used if no specific voice is found
    // or if a preferred voice is found but we want to override its natural pitch/rate.
    if (gender === 'male') {
        this.pitch = 0.5; // Aggressively low pitch for male
        this.rate = 0.85; // Slower rate for male
    } else { // female
        this.pitch = 1.1; // Slightly higher pitch for female
        this.rate = 0.92; // Slightly slower rate for female
    }

    const preferredFemaleVoices = [
        'Samantha', 
        'Google UK English Female',
        'Karen', 'Catherine', 'Martha', 'Moira', 'Tessa', 'Kathy'
    ];
    const preferredMaleVoices = [
        'Aaron',    // en-US - Top Priority
        'Daniel (English (United Kingdom))', 
        'Google UK English Male',
        'Google US English',
        'Fred',     // en-US
        'Arthur',   // en-GB
        'Gordon',   // en-AU
        'Rishi'     // en-IN
    ];

    // 1. Try to find "Aaron" specifically for male
    if (gender === 'male') {
        selectedVoice = voices.find(v => v.name === 'Aaron' && v.lang.startsWith('en'));
        if (selectedVoice) {
            this.voice = selectedVoice;
            this.pitch = 1.0; // Use natural pitch for preferred voice
            this.rate = 0.92; // Use desired thoughtful rate
            console.log(`SpeechSynthesisService: Selected preferred male voice: ${this.voice.name}`);
            return true;
        }
    }

    // 2. If "Aaron" not found (or if female), try other preferred voices
    const targetPreferredList = gender === 'female' ? preferredFemaleVoices : preferredMaleVoices;
    for (const name of targetPreferredList) {
        // Skip Aaron if gender is male as it was already checked
        if (gender === 'male' && name === 'Aaron') continue; 
        
        const found = voices.find(v => v.name === name && v.lang.startsWith('en'));
        if (found) {
            selectedVoice = found;
            this.voice = selectedVoice;
            this.pitch = 1.0; // Use natural pitch for preferred voice
            this.rate = 0.92; // Use desired thoughtful rate
            console.log(`SpeechSynthesisService: Selected preferred ${gender} voice: ${this.voice.name}`);
            return true;
        }
    }
    
    // 3. If no preferred voice found, try general keywords (local, non-Google)
    if (!selectedVoice) {
        const genderKeyword = gender === 'female' ? 'female' : 'male';
        selectedVoice = voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes(genderKeyword) && !v.name.toLowerCase().includes('google') && v.localService);
        if (selectedVoice) {
            this.voice = selectedVoice;
            // Pitch/rate already set at beginning of method for fallbacks
            console.log(`SpeechSynthesisService: Selected keyword-matched ${gender} voice: ${this.voice.name}`);
            return true;
        }
    }

    // 4. If still no voice, try Google voices (might be network dependent)
    if (!selectedVoice) {
        let googleVoiceName = '';
        if (gender === 'female') {
            googleVoiceName = 'Google UK English Female';
        } else { // male
            selectedVoice = voices.find(v => v.name === 'Google UK English Male' && v.lang.startsWith('en'));
            if (!selectedVoice) {
                googleVoiceName = 'Google US English';
            }
        }
        if (googleVoiceName) {
             selectedVoice = voices.find(v => v.name === googleVoiceName && v.lang.startsWith('en'));
             if (selectedVoice) {
                this.voice = selectedVoice;
                this.pitch = 1.0; // Use natural pitch for Google voices
                this.rate = 0.92;
                console.log(`SpeechSynthesisService: Selected Google ${gender} voice: ${this.voice.name}`);
                return true;
             }
        }
    }
    
    // 5. Final fallback: any English voice, apply aggressive pitch/rate
    if (!selectedVoice) {
        console.log(`SpeechSynthesisService: No specific ${gender} voice found. Falling back to any English voice.`);
        selectedVoice = voices.find(v => v.lang.startsWith('en') && v.localService) || 
                        voices.find(v => v.lang.startsWith('en')) || 
                        voices.find(v => v.default); // Any default voice
        if (selectedVoice) {
            this.voice = selectedVoice;
            // Pitch/rate already set at beginning of method for fallbacks
            console.log(`SpeechSynthesisService: Using generic English fallback voice: ${this.voice.name}`);
            return true;
        }
    }

    // If absolutely no voice could be found
    console.error(`SpeechSynthesisService: CRITICAL - No voices available at all for ${gender} with lang ${lang}. Speech will use system default without specific settings.`);
    this.voice = null; // Ensure voice is explicitly null if nothing found
    return false;
  }
  
  /**
   * Set speech parameters
   * @param {Object} params - Parameters object
   * @param {number} params.pitch - Speech pitch (0.1 to 2)
   * @param {number} params.rate - Speech rate (0.1 to 2)
   * @param {number} params.volume - Speech volume (0 to 1)
   */
  setParams({ pitch, rate, volume } = {}) {
    if (pitch !== undefined) this.pitch = Math.max(0.1, Math.min(2, pitch));
    if (rate !== undefined) this.rate = Math.max(0.1, Math.min(2, rate));
    if (volume !== undefined) this.volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Enable or disable sound output.
   * @param {boolean} enabled - True to enable sound, false to disable.
   */
  setSoundEnabled(enabled) {
    this.soundEnabled = enabled;
    if (!enabled && this.isSpeaking) {
      this.stop(); // Stop any current speech if sound is disabled
    }
  }
  
  /**
   * Speak the provided text
   * @param {string} text - Text to speak
   * @returns {Promise} Promise that resolves when speech completes or rejects on error
   */
  speak(text) {
    return new Promise((resolve, reject) => {
      if (!this.isSupported) {
        const error = 'Speech synthesis not supported in this browser.';
        if (this.onErrorCallback) this.onErrorCallback(new Error(error)); // Pass Error object
        reject(new Error(error));
        return;
      }

      if (!this.soundEnabled) {
        console.log("SpeechSynthesisService: Sound is disabled. Speak call ignored.");
        // Resolve immediately as if speech finished, but do nothing.
        // Call onEnd to ensure UI state (isSpeaking) is correctly updated.
        if (this.onEndCallback) this.onEndCallback();
        resolve();
        return;
      }
      
      // Cancel any ongoing speech
      this.stop();
      
      // Create a new utterance
      this.utterance = new SpeechSynthesisUtterance(text);
      
      // Set properties
      if (this.voice) this.utterance.voice = this.voice;
      this.utterance.pitch = this.pitch;
      this.utterance.rate = this.rate;
      this.utterance.volume = this.volume;

      // Critical Debug Log
      console.log(`SPEAKING with Voice: ${this.utterance.voice ? this.utterance.voice.name : 'System Default'}, Lang: ${this.utterance.voice ? this.utterance.voice.lang : 'N/A'}, Pitch: ${this.utterance.pitch}, Rate: ${this.utterance.rate}`);
      
      // Set up event handlers
      this.utterance.onstart = () => {
        this.isSpeaking = true;
        if (this.onStartCallback) this.onStartCallback();
      };
      
      this.utterance.onend = () => {
        this.isSpeaking = false;
        if (this.onEndCallback) this.onEndCallback();
        resolve();
      };
      
      this.utterance.onerror = (event) => {
        this.isSpeaking = false;
        const error = event.error || 'Unknown speech synthesis error';
        if (this.onErrorCallback) this.onErrorCallback(error);
        reject(new Error(error));
      };
      
      // Set up animation-related event handlers
      this.utterance.onboundary = (event) => {
        if (event.name === 'word') {
          const word = text.substring(event.charIndex, event.charIndex + event.charLength);
          
          // Call word boundary callbacks
          this.wordCallbacks.forEach(callback => {
            callback(word, event.charIndex, event.charLength);
          });
          
          // Estimate phonemes for mouth animation
          this.animateMouth(word);
        }
        
        if (this.onBoundaryCallback) {
          this.onBoundaryCallback(event);
        }
      };
      
      this.utterance.onmark = (event) => {
        if (this.onMarkCallback) {
          this.onMarkCallback(event);
        }
      };
      
      // Start speaking
      this.synthesis.speak(this.utterance);
    });
  }
  
  /**
   * Animate mouth based on word
   * @param {string} word - The word being spoken
   */
  animateMouth(word) {
    // Simple phoneme estimation for animation
    // In a real implementation, you might use a more sophisticated phoneme dictionary
    const phonemes = this.estimatePhonemes(word);
    
    // Call phoneme callbacks with estimated visemes
    phonemes.forEach((phoneme, index) => {
      const viseme = this.visemeMap.get(phoneme) || this.visemeMap.get('default');
      const delay = index * (100 / phonemes.length); // Distribute over ~100ms
      
      setTimeout(() => {
        this.phonemeCallbacks.forEach(callback => {
          callback(viseme, phoneme);
        });
      }, delay);
    });
  }
  
  /**
   * Estimate phonemes from a word (simplified)
   * @param {string} word - Word to estimate phonemes for
   * @returns {Array} Array of estimated phonemes
   */
  estimatePhonemes(word) {
    // This is a simplified phoneme estimator. 
    // For more accuracy, a proper phoneme dictionary or library would be needed.
    const phonemes = [];
    let lowerWord = word.toLowerCase().replace(/[^a-z\s']/g, ''); // Keep apostrophes for contractions

    // Handle some common digraphs and patterns before individual characters
    // This is not exhaustive and order matters.
    const patterns = [
        { regex: /ou|ow/, phoneme: 'oʊ' }, // as in out, cow -> maps to o-u viseme
        { regex: /ee|ea/, phoneme: 'i' },  // as in see, eat -> maps to a-e-i viseme
        { regex: /ai|ay/, phoneme: 'eɪ' }, // as in rain, say -> maps to a-e-i viseme
        { regex: /oi|oy/, phoneme: 'ɔɪ' },// as in boil, boy -> maps to o-u then a-e-i
        { regex: /sh/, phoneme: 'ʃ' },    // maps to s-z (or a new 'sh' viseme if defined)
        { regex: /ch/, phoneme: 'tʃ' },   // maps to s-z (or a new 'ch')
        { regex: /th/, phoneme: 'θ' },    // maps to th
        // Single vowel sounds (broad categories)
        { regex: /[aáàâäǎæãåā]/, phoneme: 'a' },
        { regex: /[eéèêëěẽēę]/, phoneme: 'e' },
        { regex: /[iíìîïǐįī]/, phoneme: 'i' },
        { regex: /[oóòôöǒōøœ]/, phoneme: 'o' },
        { regex: /[uúùûüǔůūų]/, phoneme: 'u' },
        // Consonants mapped to existing viseme groups
        { regex: /[fv]/, phoneme: 'f' },
        { regex: /[szʃʒtʃdʒ]/, phoneme: 's' }, // s, z, sh, ch, j sounds
        { regex: /[mnŋ]/, phoneme: 'm' }, // m, n, ng
        { regex: /[bp]/, phoneme: 'p' },  // b, p (m-b-p viseme)
        { regex: /[td]/, phoneme: 't' },  // t, d (could be like 'l' or 's' depending on art) -> default for now
        { regex: /[kg]/, phoneme: 'k' },  // k, g -> default
        { regex: /l/, phoneme: 'l' },
        { regex: /r/, phoneme: 'r' },
        { regex: /[wy]/, phoneme: 'w' }, // w, y (as in yes)
        { regex: /h/, phoneme: 'h' }     // h -> often like next vowel, or default
    ];

    let i = 0;
    while (i < lowerWord.length) {
        let matched = false;
        for (const pattern of patterns) {
            if (pattern.regex instanceof RegExp) { // If regex is complex
                 const match = lowerWord.substring(i).match(pattern.regex);
                 if (match && match.index === 0) {
                    if (pattern.phoneme === 'ɔɪ') { // Diphthong oi/oy
                        phonemes.push('o'); phonemes.push('i');
                    } else {
                        phonemes.push(pattern.phoneme);
                    }
                    i += match[0].length;
                    matched = true;
                    break;
                 }
            } else { // Simple string regex (like /sh/)
                if (lowerWord.substring(i).startsWith(pattern.regex)) {
                     if (pattern.phoneme === 'ɔɪ') {
                        phonemes.push('o'); phonemes.push('i');
                    } else {
                        phonemes.push(pattern.phoneme);
                    }
                    i += pattern.regex.length;
                    matched = true;
                    break;
                }
            }
        }
        if (!matched) {
            // If no specific pattern matches, consider it a default or silent character
            // or map common remaining consonants to a default viseme.
            // For simplicity, we can push a 'default' or skip.
            // Let's push a default for timing, can be mapped to 'closed' or a neutral.
            phonemes.push('default'); 
            i++;
        }
    }
    return phonemes.length > 0 ? phonemes : ['default']; // Ensure at least one phoneme for timing
  }
  
  /**
   * Pause speech
   */
  pause() {
    if (!this.isSupported || !this.isSpeaking) return;
    
    this.synthesis.pause();
    if (this.onPauseCallback) this.onPauseCallback();
  }
  
  /**
   * Resume speech
   */
  resume() {
    if (!this.isSupported || !this.isSpeaking) return;
    
    this.synthesis.resume();
    if (this.onResumeCallback) this.onResumeCallback();
  }
  
  /**
   * Stop speech
   */
  stop() {
    if (!this.isSupported) return;
    
    this.synthesis.cancel();
    this.isSpeaking = false;
  }
  
  /**
   * Register a callback for phoneme changes (for mouth animation)
   * @param {string} id - Unique identifier for the callback
   * @param {Function} callback - Function to call with viseme and phoneme
   */
  onPhoneme(id, callback) {
    this.phonemeCallbacks.set(id, callback);
  }
  
  /**
   * Remove a phoneme callback
   * @param {string} id - Identifier of the callback to remove
   */
  removePhonemeCallback(id) {
    this.phonemeCallbacks.delete(id);
  }
  
  /**
   * Register a callback for word boundaries
   * @param {string} id - Unique identifier for the callback
   * @param {Function} callback - Function to call with word, charIndex, and charLength
   */
  onWord(id, callback) {
    this.wordCallbacks.set(id, callback);
  }
  
  /**
   * Remove a word boundary callback
   * @param {string} id - Identifier of the callback to remove
   */
  removeWordCallback(id) {
    this.wordCallbacks.delete(id);
  }
  
  /**
   * Set callback for speech start
   * @param {Function} callback - Function to call when speech starts
   */
  onStart(callback) {
    this.onStartCallback = callback;
  }
  
  /**
   * Set callback for speech end
   * @param {Function} callback - Function to call when speech ends
   */
  onEnd(callback) {
    this.onEndCallback = callback;
  }
  
  /**
   * Set callback for speech pause
   * @param {Function} callback - Function to call when speech is paused
   */
  onPause(callback) {
    this.onPauseCallback = callback;
  }
  
  /**
   * Set callback for speech resume
   * @param {Function} callback - Function to call when speech is resumed
   */
  onResume(callback) {
    this.onResumeCallback = callback;
  }
  
  /**
   * Set callback for speech errors
   * @param {Function} callback - Function to call with error message
   */
  onError(callback) {
    this.onErrorCallback = callback;
  }
  
  /**
   * Set callback for boundary events
   * @param {Function} callback - Function to call with boundary event
   */
  onBoundary(callback) {
    this.onBoundaryCallback = callback;
  }
  
  /**
   * Set callback for mark events
   * @param {Function} callback - Function to call with mark event
   */
  onMark(callback) {
    this.onMarkCallback = callback;
  }
  
  /**
   * Check if speech synthesis is supported
   * @returns {boolean} Whether speech synthesis is supported
   */
  checkSupport() {
    return this.isSupported;
  }
}

// Export a factory function to create the service
export const createSpeechSynthesis = () => {
  return new SpeechSynthesisService();
};
